import cronstrue from "cronstrue";
import { CronExpressionParser } from "cron-parser";

/**
 * Converts a cron expression to a human-readable description.
 *
 * @param cronExpression - The cron expression (5-field format: minute hour day month weekday)
 * @returns A human-readable description of the schedule
 *
 * @example
 * getHumanReadableSchedule("0 1 * * *")
 * // Returns: "At 01:00 AM"
 *
 * @example
 * getHumanReadableSchedule("0 3 * * 3")
 * // Returns: "At 03:00 AM, only on Wednesday"
 */
export function getHumanReadableSchedule(cronExpression: string): string {
  try {
    return cronstrue.toString(cronExpression, {
      use24HourTimeFormat: false,
      verbose: true,
    });
  } catch {
    return "Invalid cron expression";
  }
}

/**
 * Calculates the next run time from a cron expression.
 *
 * @param cronExpression - The cron expression (5-field format: minute hour day month weekday)
 * @param fromDate - Optional reference date to calculate from (defaults to current time)
 * @returns The next run time as a Date object, or null if parsing fails
 *
 * @example
 * // If current time is 2026-01-04 10:00 UTC
 * getNextRunTime("0 1 * * *")
 * // Returns: Date for 2026-01-05 01:00 UTC (next day at 1 AM)
 *
 * @example
 * getNextRunTime("0 3 * * 3")
 * // Returns: Date for next Wednesday at 3 AM UTC
 */
export function getNextRunTime(cronExpression: string, fromDate?: Date): Date | null {
  try {
    const cron = CronExpressionParser.parse(cronExpression, {
      currentDate: fromDate ?? new Date(),
      utc: true,
    });
    const nextDate = cron.next();
    // Return a new Date to ensure it's a native Date object
    return new Date(nextDate.getTime());
  } catch {
    return null;
  }
}
